import { useEffect, useState } from "react";

/**
 * Loads the Google Maps JavaScript API once for the whole app, reusing the
 * existing `VITE_GOOGLE_MAPS_KEY` (already used by CustomerAvatar's Street View
 * Static API). Powers the Board day-of map (BOARD-MAP-01) and, later, the
 * SEG-01 route leg.
 *
 * NOTE (sandbox-down constraint): we intentionally avoid the
 * `@types/google.maps` dependency (can't `npm install` while the Linux sandbox
 * is offline) and type the SDK loosely at the call sites via `window.google`.
 * When the sandbox is back, adding `@types/google.maps` and tightening the
 * `any`s is a clean follow-up.
 *
 * PREREQUISITE (Google Cloud, one-time, owner action): the key must have the
 * **Maps JavaScript API** enabled AND the host referrer allowed
 * (`http://localhost:5173/*` for dev, plus the eventual prod host). The Street
 * View Static API alone — which the avatar uses — is NOT sufficient for this.
 * If it isn't enabled, `state` resolves to "error" and the map shows a hint.
 */

type LoadState = "idle" | "loading" | "ready" | "error" | "no-key";

// Module-level singleton so the script is injected exactly once even if many
// maps mount/unmount.
let scriptPromise: Promise<void> | null = null;

function mapsReady(): boolean {
  // Gate on the actual `Map` constructor, not just the `google.maps` namespace.
  // (With the async bootstrap the namespace appears before the constructor is
  // loaded — checking only `.maps` reports ready too early and `new Map()`
  // throws.)
  return Boolean(
    (window as unknown as { google?: { maps?: { Map?: unknown } } }).google?.maps
      ?.Map,
  );
}

function loadScript(key: string): Promise<void> {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    if (mapsReady()) {
      resolve();
      return;
    }
    const existing = document.getElementById(
      "gmaps-js",
    ) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error("Google Maps failed to load")),
      );
      return;
    }
    const s = document.createElement("script");
    s.id = "gmaps-js";
    s.async = true;
    s.defer = true;
    // Classic loader (no `loading=async`): defines `google.maps.Map`
    // synchronously by the time `load` fires, so callers can use the
    // constructor directly without `importLibrary`.
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      key,
    )}`;
    s.addEventListener("load", () => resolve());
    s.addEventListener("error", () =>
      reject(new Error("Google Maps failed to load")),
    );
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export function useGoogleMaps(): { state: LoadState; ready: boolean } {
  const key = import.meta.env.VITE_GOOGLE_MAPS_KEY as string | undefined;
  const [state, setState] = useState<LoadState>(() =>
    !key ? "no-key" : mapsReady() ? "ready" : "idle",
  );

  useEffect(() => {
    if (!key) {
      setState("no-key");
      return;
    }
    if (mapsReady()) {
      setState("ready");
      return;
    }
    let cancelled = false;
    setState("loading");
    loadScript(key)
      .then(() => {
        if (!cancelled) setState("ready");
      })
      .catch(() => {
        if (!cancelled) setState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [key]);

  return { state, ready: state === "ready" };
}
